"use client";
import React, { forwardRef } from "react";
import { cn } from "../../lib/utilsComprehensive";
import { useA11yId } from "@/utils/a11y";
import { useMotionPreferenceContext } from "@/contexts/MotionPreferenceContext";
import {
  ContrastGuard,
  TextWithContrast,
} from "@/components/accessibility/ContrastGuard";

export interface GlassStackProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Stack direction
   */
  direction?: "vertical" | "horizontal";
  /**
   * Responsive direction
   */
  responsive?: {
    sm?: "vertical" | "horizontal";
    md?: "vertical" | "horizontal";
    lg?: "vertical" | "horizontal";
    xl?: "vertical" | "horizontal";
    "2xl"?: "vertical" | "horizontal";
  };
  /**
   * Space between items
   */
  space?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  /**
   * Alias for space (compat)
   */
  spacing?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  /**
   * Responsive spacing
   */
  responsiveSpace?: {
    sm?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
    md?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
    lg?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
    xl?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
    "2xl"?: "none" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  };
  /**
   * Alignment of items
   */
  align?: "start" | "center" | "end" | "stretch" | "baseline";
  /**
   * Justify content
   */
  justify?: "start" | "center" | "end" | "between" | "around" | "evenly";
  /**
   * Whether items should wrap
   */
  wrap?: boolean;
  /**
   * Divider between items
   */
  divider?: React.ReactNode;
  /**
   * Custom divider render function
   */
  renderDivider?: (index: number) => React.ReactNode;
  /**
   * Whether to respect user's motion preferences
   */
  respectMotionPreference?: boolean;
  /**
   * Accessibility label for screen readers
   */
  "aria-label"?: string;
  /**
   * Accessibility role for semantic meaning
   */
  role?: string;
}

export interface GlassStackItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Whether this item should grow to fill available space
   */
  grow?: boolean;
  /**
   * Whether this item should shrink
   */
  shrink?: boolean;
  /**
   * Align this item differently from the stack alignment
   */
  alignSelf?: "auto" | "start" | "center" | "end" | "stretch" | "baseline";
  /**
   * Whether to respect user's motion preferences
   */
  respectMotionPreference?: boolean;
  /**
   * Accessibility label for screen readers
   */
  "aria-label"?: string;
  /**
   * Accessibility role for semantic meaning
   */
  role?: string;
}

/**
 * GlassStack component
 * Layout component for stacking items with consistent spacing
 */
export const GlassStack = forwardRef<HTMLDivElement, GlassStackProps>(
  (
    {
      direction = "vertical",
      responsive,
      space = "md",
      spacing,
      responsiveSpace,
      align = "stretch",
      justify = "start",
      wrap = false,
      divider,
      renderDivider,
      respectMotionPreference = true,
      "aria-label": ariaLabel,
      role,
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    const spaceValues = {
      none: "0",
      xs: "var(--glass-space-1, 0.25rem)",
      sm: "var(--glass-space-2, 0.5rem)",
      md: "var(--glass-space-4, 1rem)",
      lg: "var(--glass-space-6, 1.5rem)",
      xl: "var(--glass-space-8, 2rem)",
      "2xl": "var(--glass-space-12, 3rem)",
    } as const;

    const alignValues = {
      start: "flex-start",
      center: "center",
      end: "flex-end",
      stretch: "stretch",
      baseline: "baseline",
    } as const;

    const justifyValues = {
      start: "flex-start",
      center: "center",
      end: "flex-end",
      between: "space-between",
      around: "space-around",
      evenly: "space-evenly",
    } as const;

    const directionValues = {
      vertical: "column",
      horizontal: "row",
    } as const;

    const spaceClasses = {
      none: "glass-gap-0",
      xs: "glass-gap-1",
      sm: "glass-gap-2",
      md: "glass-gap-4",
      lg: "glass-gap-6",
      xl: "glass-gap-8",
      "2xl": "glass-gap-12",
    };

    const alignClasses = {
      start: "glass-items-start",
      center: "glass-items-center",
      end: "glass-items-end",
      stretch: "glass-items-stretch",
      baseline: "glass-items-baseline",
    };

    const justifyClasses = {
      start: "glass-justify-start",
      center: "glass-justify-center",
      end: "glass-justify-end",
      between: "glass-justify-between",
      around: "glass-justify-around",
      evenly: "glass-justify-evenly",
    };

    const directionClasses = {
      vertical: "glass-flex-col",
      horizontal: "glass-flex-row",
    };

    const responsiveDirectionClasses = responsive
      ? [
          responsive.sm && `sm:${directionClasses[responsive.sm]}`,
          responsive.md && `md:${directionClasses[responsive.md]}`,
          responsive.lg && `lg:${directionClasses[responsive.lg]}`,
          responsive.xl && `xl:${directionClasses[responsive.xl]}`,
          responsive["2xl"] && `2xl:${directionClasses[responsive["2xl"]]}`,
        ].filter(Boolean)
      : [];

    const responsiveSpaceClasses = responsiveSpace
      ? [
          responsiveSpace.sm && `sm:${spaceClasses[responsiveSpace.sm]}`,
          responsiveSpace.md && `md:${spaceClasses[responsiveSpace.md]}`,
          responsiveSpace.lg && `lg:${spaceClasses[responsiveSpace.lg]}`,
          responsiveSpace.xl && `xl:${spaceClasses[responsiveSpace.xl]}`,
          responsiveSpace["2xl"] &&
            `2xl:${spaceClasses[responsiveSpace["2xl"]]}`,
        ].filter(Boolean)
      : [];

    // Process children to add dividers
    const processedChildren = React.Children.toArray(children).filter(Boolean);

    const childrenWithDividers =
      divider || renderDivider
        ? processedChildren.reduce((acc: React.ReactNode[], child, index) => {
            acc.push(child);

            // Add divider between items (not after the last item)
            if (index < processedChildren.length - 1) {
              const dividerElement = renderDivider
                ? renderDivider(index)
                : divider;
              if (dividerElement) {
                acc.push(
                  <div key={`divider-${index}`} className="glass-flex-shrink-0">
                    {dividerElement}
                  </div>
                );
              }
            }

            return acc;
          }, [] as React.ReactNode[])
        : processedChildren;

    const stackId = useA11yId();
    const { prefersReducedMotion } = useMotionPreferenceContext();
    const shouldRespectMotion =
      respectMotionPreference && !prefersReducedMotion;

    const a11yProps = {
      ...(ariaLabel && { "aria-label": ariaLabel, id: stackId }),
      ...(role && { role }),
    };

    const stackStyle: React.CSSProperties = {
      display: "flex",
      flexDirection: directionValues[direction],
      gap: spaceValues[spacing ?? space],
      alignItems: alignValues[align],
      justifyContent: justifyValues[justify],
      flexWrap: wrap ? "wrap" : "nowrap",
      minWidth: 0,
      ...style,
    };

    return (
      <div
        ref={ref}
        className={cn(
          "glass-flex glass-min-w-0",
          directionClasses[direction],
          ...responsiveDirectionClasses,
          spaceClasses[spacing ?? space],
          ...responsiveSpaceClasses,
          alignClasses[align],
          justifyClasses[justify],
          wrap && "glass-flex-wrap",
          // Motion preferences
          shouldRespectMotion &&
            "motion-safe:transition-all motion-reduce:transition-none",
          className
        )}
        style={stackStyle}
        {...a11yProps}
        {...props}
      >
        {childrenWithDividers}
      </div>
    );
  }
);

GlassStack.displayName = "GlassStack";

/**
 * GlassStackItem component
 * Individual item within a stack with flex control
 */
export const GlassStackItem = forwardRef<HTMLDivElement, GlassStackItemProps>(
  (
    {
      grow = false,
      shrink = true,
      alignSelf = "auto",
      respectMotionPreference = true,
      "aria-label": ariaLabel,
      role,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const alignSelfClasses = {
      auto: "glass-self-auto",
      start: "glass-self-start",
      center: "glass-self-center",
      end: "glass-self-end",
      stretch: "glass-self-stretch",
      baseline: "glass-self-baseline",
    };

    const alignSelfValues = {
      auto: "auto",
      start: "flex-start",
      center: "center",
      end: "flex-end",
      stretch: "stretch",
      baseline: "baseline",
    } as const;

    const itemId = useA11yId();
    const { prefersReducedMotion } = useMotionPreferenceContext();
    const shouldRespectMotion =
      respectMotionPreference && !prefersReducedMotion;

    const a11yProps = {
      ...(ariaLabel && { "aria-label": ariaLabel, id: itemId }),
      ...(role && { role }),
    };

    return (
      <div
        ref={ref}
        className={cn(
          grow && "glass-flex-grow",
          !shrink && "glass-flex-shrink-0",
          alignSelfClasses[alignSelf],
          // Motion preferences
          shouldRespectMotion &&
            "motion-safe:transition-all motion-reduce:transition-none",
          className
        )}
        style={{
          flexGrow: grow ? 1 : undefined,
          flexShrink: shrink ? 1 : 0,
          alignSelf: alignSelfValues[alignSelf],
          minWidth: 0,
          ...style,
        }}
        {...a11yProps}
        {...props}
      />
    );
  }
);

GlassStackItem.displayName = "GlassStackItem";

/**
 * VStack component
 * Vertical stack (shorthand for GlassStack with direction="vertical")
 */
export const VStack = forwardRef<
  HTMLDivElement,
  Omit<GlassStackProps, "direction">
>((props, ref) => <GlassStack ref={ref} direction="vertical" {...props} />);

VStack.displayName = "VStack";

/**
 * HStack component
 * Horizontal stack (shorthand for GlassStack with direction="horizontal")
 */
export const HStack = forwardRef<
  HTMLDivElement,
  Omit<GlassStackProps, "direction">
>((props, ref) => <GlassStack ref={ref} direction="horizontal" {...props} />);

HStack.displayName = "HStack";
